import React from "react";
import { Card, CardBody, Heading, Badge } from "@aidkitorg/component-library";
import type { TargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/targetFieldsWorkflow";
import WorkflowReview from "./WorkflowReview";

export default function TargetFieldsWorkflowReview({ distroCommitId }: { distroCommitId: string }) {
  
  const renderTargetFieldsResults = (workflow: any) => {
    if (!workflow?.result?.success) {
      return null;
    }

    const result = workflow.result as TargetFieldsWorkflowResult;

    return (
      <div className="space-y-6">
        {/* Summary */}
        <Card>
          <CardBody>
            <Heading level="h3" className="text-md font-semibold mb-3" id="target-fields-summary">
              Summary
            </Heading>
            <dl className="grid grid-cols-2 gap-4 text-sm" aria-labelledby="target-fields-summary">
              <div>
                <dt className="font-medium">Affected Fields</dt>
                <dd>{result.targetFields?.filter(field => field.countChangedApplicants > 0).length || 0}</dd>
              </div>
              <div>
                <dt className="font-medium">Affected Applicants</dt>
                <dd>{result.changedApplicants.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="font-medium">Total Fields</dt>
                <dd>{result.targetFields?.length || 0}</dd>
              </div>
              <div>
                <dt className="font-medium">Total Applicants</dt>
                <dd>{result.totalApplicants.toLocaleString()}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* Field Changes Details */}
        {result.targetFields && result.targetFields.length > 0 && (
          <Card className="max-w-4xl">
            <CardBody>
              <Heading level="h3" className="text-md font-semibold mb-3" id="field-changes-details">
                Value Changes
              </Heading>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" aria-labelledby="field-changes-details">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th scope="col" className="text-left py-2 pr-4 font-medium">
                        Field Name
                      </th>
                      <th scope="col" className="text-right py-2 font-medium">
                        Affected Applicants
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.targetFields.map((field) => {
                      const hasChanges = field.countChangedApplicants > 0;
                      const percentageImpact = result.totalApplicants > 0 
                        ? Math.round((field.countChangedApplicants / result.totalApplicants) * 100)
                        : 0;
                      
                      return (
                        <tr 
                          key={field.key}
                          className={`border-b border-gray-100 hover:bg-gray-50 ${hasChanges ? 'bg-yellow-100' : ''}`}
                        >
                          <td className="py-3 px-4">
                            <div className="font-medium">{field.key}</div>
                          </td>
                          <td className="py-3 text-right px-4">
                            <div className="font-medium">
                              {field.countChangedApplicants.toLocaleString()}
                              {hasChanges && (
                                <span className="text-sm text-gray-600 ml-2">
                                  ({percentageImpact}%)
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="target_fields"
      title="Target Fields Analysis"
      renderSummary={renderTargetFieldsResults}
    />
  );
} 
